import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { User, UserWhereInput } from '@lgg/isomorphic/types/__generated__/graphql';
import {
  getSortedUserSelectOptions,
  groupByRoleId,
} from 'src/components/domain/users/hooks/helpers';
import {
  UseCompanyUsersReturn,
  useCompanyUsers,
} from 'src/components/domain/users/hooks/use-company-users';
import { userToSelectOptionMapper } from 'src/components/general/inputs/select/mappers/user-to-select-option-mapper';
import { SelectOption } from 'src/components/general/inputs/select/select';

type UseUserListForSelectReturn = {
  groupedOptions: { label: string; options: SelectOption<number>[] }[];
  groupedUsers: { label: string; users: User[] }[];
  loading: boolean;
  options: SelectOption<number>[];
  users: User[];
} & Pick<UseCompanyUsersReturn, 'refetch' | 'loadMore' | 'hasNextPage'>;

export const useCompanyUsersListForSelect = (
  where?: UserWhereInput,
): UseUserListForSelectReturn => {
  const { t } = useTranslation(['common']);
  const { users, loading, refetch, loadMore, hasNextPage } = useCompanyUsers({
    where,
  });

  const activeUsers = users.filter(({ isActive }) => isActive);

  const groupedUsers = useMemo(
    () =>
      groupByRoleId(activeUsers, t).map(({ label, items }) => ({
        label,
        users: items,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeUsers, t],
  );

  const groupedOptions = useMemo(() => {
    const options = groupedUsers.map(({ label, users }) => ({
      label,
      options: users.map(userToSelectOptionMapper),
    }));

    return options;
  }, [groupedUsers]);

  const userOptions = getSortedUserSelectOptions(users);

  return {
    groupedOptions,
    groupedUsers,
    hasNextPage,
    loading: loading,
    loadMore,
    options: userOptions,
    refetch,
    users,
  };
};
